import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckoutButton } from "./CheckoutButton";
import { ShareResult } from "./ShareResult";
import { P_ORDER, quiz, nextStepMap } from "@/data/content";
import { useSite } from "@/context/SiteContent";
import { scrollToId } from "@/lib/scroll";

const ease = [0.2, 0.7, 0.2, 1];
const zeroScores = () => ({ purpose: 0, people: 0, program: 0, place: 0, profit: 0 });

const Radar = ({ values, primary }) => {
  const labels = ["Purpose", "People", "Program", "Place", "Profit"];
  const cx = 180, cy = 165, maxR = 112;
  const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / 5;
  const pt = (r, i) => [cx + Math.cos(angle(i)) * r, cy + Math.sin(angle(i)) * r];
  const polygon = (r) => P_ORDER.map((_, i) => pt(r, i).join(",")).join(" ");
  const profile = P_ORDER.map((k, i) => pt(maxR * (values[k] / 5), i));
  return (
    <svg className="radar-svg" viewBox="0 0 360 330" role="img" aria-label="5Ps focus radar chart" data-testid="quiz-radar">
      {[1, 2, 3, 4, 5].map((l) => <motion.polygon key={l} className="radar-grid" points={polygon((maxR * l) / 5)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: l * 0.06 }} />)}
      {P_ORDER.map((_, i) => { const [x, y] = pt(maxR, i); return <line key={i} className="radar-axis" x1={cx} y1={cy} x2={x} y2={y} />; })}
      <motion.g style={{ transformOrigin: `${cx}px ${cy}px`, transformBox: "view-box" }} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.3, ease }}>
        <polygon className="radar-profile" points={profile.map((p) => p.join(",")).join(" ")} />
      </motion.g>
      {profile.map((p, i) => <motion.circle key={i} className="radar-point" cx={p[0]} cy={p[1]} r={P_ORDER[i] === primary ? 5 : 4} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.1 + i * 0.08 }} style={{ transformOrigin: `${p[0]}px ${p[1]}px`, transformBox: "view-box" }}><title>{labels[i]}: {values[P_ORDER[i]].toFixed(1)} / 5</title></motion.circle>)}
      {P_ORDER.map((k, i) => {
        const [x, y] = pt(maxR + 34, i);
        const vy = i === 0 ? -8 : i === 2 || i === 3 ? 9 : 3;
        return (
          <g key={k}>
            <text className="radar-label" x={x} y={y + vy}>{labels[i]}</text>
            <text className="radar-value" x={x} y={y + vy + 15}>{values[k].toFixed(1)}</text>
          </g>
        );
      })}
    </svg>
  );
};

export const Quiz = ({ setActiveP, onResult }) => {
  const { fivePs, price } = useSite();
  const pData = fivePs.items;
  const [qi, setQi] = useState(0);
  const [scores, setScores] = useState(zeroScores);
  const [picked, setPicked] = useState(null);
  const done = qi >= quiz.length;

  const answer = (k) => {
    if (picked) return;
    setPicked(k);
    setTimeout(() => {
      const next = { ...scores, [k]: scores[k] + 1 };
      setScores(next);
      setQi(qi + 1);
      setPicked(null);
      if (qi + 1 >= quiz.length) {
        const primary = Object.entries(next).sort((a, b) => b[1] - a[1])[0][0];
        onResult(pData[primary].name);
      }
    }, 260);
  };
  const reset = () => { setQi(0); setScores(zeroScores()); setPicked(null); };

  let result = null;
  if (done) {
    const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const primary = ranked[0][0], secondary = ranked[1][0];
    const maxCount = Math.max(...Object.values(scores), 1);
    const radar = {};
    P_ORDER.forEach((k) => { radar[k] = Math.round((1 + 4 * (scores[k] / maxCount)) * 10) / 10; });
    result = { primary, secondary, radar, p: pData[primary], s: pData[secondary] };
  }

  return (
    <section className="quiz-section" data-rail="5Ps Quiz" id="quiz" data-testid="quiz-section">
      <div className="container quiz-grid">
        <div className="reveal">
          <div className="eyebrow">Quick. Personalised. Actionable.</div>
          <h2 style={{ color: "white", marginTop: 10 }}>Which of the 5Ps deserves more attention right now?</h2>
          <p className="lead" style={{ color: "rgba(255,255,255,.72)" }}>Five questions based on Scott's Campus2Corp themes. Your result points you toward a primary P, a secondary P and lesson topics that are likely to feel most relevant.</p>
        </div>
        <div className="quiz-card reveal">
          {!done ? (
            <div id="quizForm" data-testid="quiz-form">
              <div style={{ display: "flex", justifyContent: "space-between", color: "rgba(255,255,255,.55)", fontSize: ".78rem" }}>
                <span data-testid="quiz-step">Q{qi + 1} / {quiz.length}</span><span>5Ps check-in</span>
              </div>
              <div className="quiz-progress"><motion.i animate={{ width: `${((qi + 1) / quiz.length) * 100}%` }} transition={{ duration: 0.5, ease }} /></div>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div key={qi} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3, ease }}>
                  <div className="quiz-question" data-testid="quiz-question">{quiz[qi].q}</div>
                  <div className="quiz-options">
                    {quiz[qi].a.map(([t, k], i) => (
                      <motion.button key={k} className={`quiz-option${picked === k ? " is-picked" : ""}`} onClick={() => answer(k)} data-testid={`quiz-option-${k}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 + i * 0.05 }}>
                        <span className="quiz-option-key">{String.fromCharCode(65 + i)}</span>{t}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          ) : (
            <motion.div className="quiz-result" style={{ display: "block" }} data-testid="quiz-result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}>
              <div className="result-header">
                <div>
                  <div className="eyebrow">Your 5Ps focus profile</div>
                  <div className="result-title" data-testid="quiz-result-title">{result.p.name}</div>
                  <p className="result-subtitle">Your answers point most strongly towards <strong>{result.p.name}</strong>, with <strong>{result.s.name}</strong> as a secondary focus.</p>
                </div>
                <span className="result-badge">Personalised result</span>
              </div>
              <div className="result-dashboard">
                <div className="radar-card">
                  <div className="radar-title">Your current 5Ps focus</div>
                  <div className="radar-sub">Scale: 1 (lower focus) to 5 (higher focus)</div>
                  <div className="radar-wrap"><Radar values={result.radar} primary={result.primary} /></div>
                  <div className="radar-note">This reflects where your quiz answers clustered. It is a reflection profile, not a professional assessment or skill score.</div>
                </div>
                <div className="result-insights">
                  <div className="insight-block"><span className="insight-kicker">What this may mean</span><p>{result.p.text}</p></div>
                  <div className="lesson-rec">
                    <strong>Start with these Campus2Corp themes:</strong><br />{result.p.tags.slice(0, 4).join(" • ")}<br />
                    <span style={{ color: "rgba(255,255,255,.52)" }}>Secondary area: {result.s.name}</span>
                  </div>
                  <div className="next-step-card">
                    <span className="insight-kicker">Recommended next step</span>
                    <strong>{nextStepMap[result.primary][0]}</strong>
                    <p>{nextStepMap[result.primary][1]}</p>
                  </div>
                </div>
              </div>
              <div className="result-score-row">
                {P_ORDER.map((k) => <div className="result-score" key={k}><span>{pData[k].name}</span><strong>{result.radar[k].toFixed(1)}</strong></div>)}
              </div>
              <ShareResult result={result} />
              <div className="result-actions">
                <button className="btn btn-gold" onClick={() => { setActiveP(result.primary); scrollToId("fiveps"); }} data-testid="quiz-explore-btn">Explore My Primary P</button>
                <CheckoutButton className="btn btn-outline" testId="quiz-checkout-btn">Get the eBook • {price}</CheckoutButton>
                <button className="btn btn-outline" onClick={reset} data-testid="quiz-retake-btn">Retake Quiz</button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
